import React, { useEffect, useRef, useState, useContext } from 'react';
import { Box, Grid, Text, ResponsiveContext, Avatar } from 'grommet';
import { getWeekScoreboard } from '../espnReader';
import { totalScore } from '../util';
import TeamName from './TeamName';

const LeagueScoreboard = ({ leagueId, season, refreshKey }) => {
  const [matchups, setMatchups] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const hasLoaded = useRef(false);
  const size = useContext(ResponsiveContext); // Get screen size (small, medium, large, etc.)

  useEffect(() => {
    let cancelled = false;
    // Fetch the current week's matchups with team info
    // A live refresh updates in place: no loading flash, and a failed
    // refresh keeps the last good scores on screen.
    const isRefresh = hasLoaded.current;
    const fetchMatchups = async () => {
      if (!isRefresh) setStatus('loading');
      try {
        const games = await getWeekScoreboard(leagueId, season);
        if (cancelled) return;
        if (!Array.isArray(games)) {
          if (!isRefresh) setStatus('error');
          return;
        }
        setMatchups(games);
        hasLoaded.current = true;
        setStatus('ready');
      } catch (error) {
        if (!cancelled && !isRefresh) setStatus('error');
      }
    };
    fetchMatchups();
    return () => { cancelled = true; };
  }, [leagueId, season, refreshKey]);

  // Determine if we should use the compact layout (for small screens)
  const isSmallScreen = size === 'small' || size === 'xsmall';

  if (status === 'loading') return <Text>Loading matchups…</Text>;
  if (status === 'error') return <Text color="status-critical">Couldn't load matchups.</Text>;

  return (
      <Box pad={isSmallScreen ? 'auto' : 'small'}>
        {matchups.length > 0 ? (
          <Grid
            rows={isSmallScreen ? 'auto' : 'small'} // Use compact rows for small screens
            columns={isSmallScreen ? '100%' : ['1/2', '1/2']}
            gap={isSmallScreen ? 'small' : 'medium'} // Adjust gap based on screen size
            responsive
          >
            {matchups.map((game, index) => (
              <Box
                key={game.id}
                border={{ color: 'brand', size: 'small' }}
                round="small"
                pad={isSmallScreen ? { vertical: 'xsmall', horizontal: 'small' } : { vertical: 'small', horizontal: 'medium' }} // Conditionally adjust padding
                background="light-2"
              >
                <Grid
                  rows={isSmallScreen ? ['xxsmall', 'xxsmall'] : ['small', 'small']} // Adjust row height based on screen size
                  columns={['1/2', '1/2']}
                  gap={isSmallScreen ? 'xsmall' : 'medium'} // Adjust gap between home and away teams
                  areas={[
                    { name: 'awayTeam', start: [0, 0], end: [1, 0] },
                    { name: 'homeTeam', start: [0, 1], end: [1, 1] },
                  ]}
                >
                  {/* Away Team */}
                  <Box gridArea="awayTeam" direction="row" align="center" gap="small">
                    <Avatar src={game.away.teamLogo} alt={game.away.teamName} size={isSmallScreen ? 'small' : 'medium'} /> {/* Adjust avatar size */}
                    <Box>
                      <TeamName name={game.away.teamName} record={game.away.teamRecord} size={isSmallScreen ? 'medium' : 'large'} />
                      <Text size={isSmallScreen ? 'medium' : 'large'} weight="bold">
                        {Math.round(totalScore(game.away) * 100) / 100}
                      </Text>
                    </Box>
                  </Box>

                  {/* Home Team */}
                  <Box gridArea="homeTeam" direction="row" align="center" gap="small">
                    <Avatar src={game.home.teamLogo} alt={game.home.teamName} size={isSmallScreen ? 'small' : 'medium'} /> {/* Adjust avatar size */}
                    <Box>
                      <TeamName name={game.home.teamName} record={game.home.teamRecord} size={isSmallScreen ? 'medium' : 'large'} />
                      <Text size={isSmallScreen ? 'medium' : 'large'} weight="bold">
                        {Math.round(totalScore(game.home) * 100) / 100}
                      </Text>
                    </Box>
                  </Box>
                </Grid>
              </Box>
            ))}
          </Grid>
        ) : (
          <Text>No matchups this week.</Text>
        )}
      </Box>
  );
};

export default LeagueScoreboard;